import {
  CodeInputForm,
  IllustrationFrame,
  ProgressDots,
  useRedemptionState,
} from '@/features/code-redemption';
import { ParticipantAutoIssue, useParticipantBootstrap } from '@/features/participant';
import { DownloadCard } from '@/features/reward';
import { ScheduleTimeline } from '@/features/streamer-schedule';
import { NotebookSheet } from '@/shared/ui';

import styles from './HomePage.module.css';

/**
 * 홈(코드 입력) 페이지.
 *
 * 구조:
 *  1. Hero — 행사 소개, 진행 점, 메타 라인.
 *  2. 일러스트 + 코드 입력 (2단 그리드).
 *  3. 방송 타임라인 — 3인 + 합방 피날레 (실시간 LIVE 갱신).
 *
 * 종이 그레인 배경 위에 마스킹 테이프·핸드라이팅 라벨 같은 데코를 절대 위치로 살짝 어긋나게 배치한다.
 * 3/3 완료 시 자동으로 `/reward`로 리다이렉트.
 */
export function HomePage(): React.JSX.Element {
  const { state, isComplete } = useRedemptionState();
  // 부팅 시 저장된 userCode 의 유효성 검증 + 서버 카운트 동기화.
  useParticipantBootstrap();
  const filled = Math.min(state.codeCount, 3);

  return (
    <main className={styles.wrap}>
      {/* === Hero === */}
      <header className={styles.hero}>
        {/*
         * 다락캐스트 MCN 로고 — Hero 최상단에 배치하여 행사 주최자(MCN)를 우선 노출한다.
         * "주최(로고) → 행사명(eyebrow) → 메인 카피(title)"의 위계로 시선이 자연스럽게 흐르며,
         * 노트/종이 컨셉의 페이지에서 표지 스티커처럼 어울리도록 작은 사이즈로 가운데 정렬한다.
         */}
        <img src="/logo.png" alt="다락캐스트" className={styles.brandLogo} />
        <span className={styles.eyebrow}>2026 다락캐스트 어린이날 릴레이 방송</span>
        <h1 className={styles.title}>
          3명이 이어 가는
          <br />
          <span className={styles.accent}>한 폭의 어린이날</span>
        </h1>
        <p className={styles.lead}>
          다락캐스트 소속 크리에이터 3인의 릴레이 방송에서 코드를 모아주세요.
          <br className={styles.brDesktop} />
          {' '}방송당 하나의 코드를 획득할 수 있고, 모두 모으면 한정 일러스트를 받을 수 있어요!
        </p>
        {/* 참여자 코드 자동 발급 위젯 — 진입 시 즉시 발급되며, 발급 후엔 한 줄 인라인 배지로
           남아 있어 본문 영역을 차지하지 않는다. "이어서 하기" 진입점도 이 위젯에 통합. */}
        <div className={styles.participantBadgeRow}>
          <ParticipantAutoIssue />
        </div>
      </header>
{/*
      <Divider /> */}

      {/* === 이번 방송 컨셉 안내 === */}
      {/* <ConceptIntro /> */}

      {/* === 일러스트 + 입력 === */}
      <section className={styles.assembly} aria-label="코드 입력 및 일러스트 조립">
        <div className={styles.illustration}>
          <IllustrationFrame
            revealedCount={filled}
            trailing={<ProgressDots filled={filled} total={3} />}
          />
        </div>

        {isComplete ? (
          <DownloadCard />
        ) : (
          <NotebookSheet marginColor="none" ruled={false} className={styles.formCard}>
            <div className={styles.cardHeading}>
              <span className={styles.cardHeadingTitle}>코드 입력</span>
            </div>
            <CodeInputForm step={Math.min(filled + 1, 3)} />
            <div className={styles.help}>
              <p>
                코드는 방송 화면에 랜덤으로 노출됩니다. <br></br>
                지난 코드는 합방 시간을 노려보세요!
              </p>
            </div>
          </NotebookSheet>
        )}
      </section>

      <Divider />

      {/* === 방송 타임라인 === */}
      <ScheduleTimeline />

      <Divider />

      {/* === 참여 가이드라인 === */}
      <Guidelines />

      <footer className={styles.footer}>
       © 2026 다락캐스트. All rights reserved.
      </footer>
    </main>
  );
}

/**
 * 손글씨 톤의 섹션 디바이더.
 *
 * 인라인 SVG 패스를 `repeat-x` 배경으로 깔아 종이에 그어진 잉크 라인 같은 인상을 만든다.
 */
function Divider(): React.JSX.Element {
  return <hr className={styles.divider} aria-hidden />;
}

/**
 * 참여 가이드라인을 Q&A 아코디언으로 보여주는 섹션.
 *
 * 각 질문은 네이티브 `<details>`/`<summary>` 요소로 구성되어 클릭하면 답변이 펼쳐진다.
 * 자바스크립트 상태 없이도 키보드 접근성이 보장되며, 페이지 어디서나 일관된
 * "한 항목씩 열어 보는" UX를 제공한다. 시각은 노트/크레파스 컨셉과 분리해
 * 흰 배경 + 가는 보더 카드로 정보 가독성에 집중한다.
 */
function Guidelines(): React.JSX.Element {
  return (
    <section className={styles.guidelines} aria-labelledby="guidelines-title">
      <h2 id="guidelines-title" className={styles.guidelinesTitle}>
        참여 가이드라인
      </h2>
      <p className={styles.guidelinesLead}>궁금한 항목을 눌러 답변을 확인하세요.</p>

      <ul className={styles.faqList}>
        {GUIDELINES.map((item) => (
          <li key={item.q} className={styles.faqItem}>
            <details className={styles.faq}>
              <summary className={styles.faqSummary}>
                <span className={styles.faqQuestion}>{item.q}</span>
                <span className={styles.faqIcon} aria-hidden />
              </summary>
              <div className={styles.faqAnswer}>{item.a}</div>
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 참여 가이드라인 Q&A 데이터.
 *
 * `q`는 사용자가 클릭해서 펼치는 질문, `a`는 펼친 뒤 표시되는 답변이다.
 * 카피는 운영 정책에 맞춰 자유롭게 수정 가능하며, 순서가 곧 표시 순서다.
 */
const GUIDELINES: readonly { q: string; a: string }[] = [
  {
    q: '누구나 참여할 수 있나요?',
    a: '네, 누구나 무료로 참여할 수 있어요. 별도 가입 없이 참여자 코드를 발급받고, 방송에서 얻은 코드를 입력하시면 됩니다.',
  },
  {
    q: '참여자 코드는 무엇인가요?',
    a: '참여자 본인을 식별하는 6자리 고유 코드예요. 페이지에 처음 들어오면 자동으로 발급받을 수 있고, 다른 브라우저나 모바일에서도 이 코드만 있으면 같은 진행 상태로 이어서 참여할 수 있어요.',
  },
  {
    q: '다른 디바이스에서 이어서 할 수 있나요?',
    a: '가능해요. 새 디바이스에서 페이지를 열고 "이어서 하기" 탭에 발급받았던 6자리 코드를 입력하면, 그동안 모은 코드 진행 상태가 그대로 복원됩니다. 이미 다른 코드로 발급한 상태라면, 화면 상단의 "내 코드" 배지를 눌러 코드를 교체할 수 있어요.',
  },
  {
    q: '참여자 코드를 잊어버리면 어떻게 하나요?',
    a: '참여자 코드는 발급 시 한 번만 표시되며, 분실하면 진행 상태를 복원할 수 없어요. 발급 직후 메모하거나 캡처해 안전하게 보관해 주세요. 분실한 경우 다락캐스트 공식 채널로 문의해 주세요.',
  },
  {
    q: '행사는 언제 진행되나요?',
    a: '2026년 5월 5일 어린이날, 다락캐스트 크리에이터 3인의 릴레이 방송과 합방 방송 동안 진행돼요.',
  },
  {
    q: '코드는 어디서 얻을 수 있나요?',
    a: '각 방송 화면에 랜덤한 시점으로 코드가 노출됩니다. 방송당 1개의 코드를 획득할 수 있고, 지난 코드는 합방 시간을 노려보세요.',
  },
  {
    q: '보상은 어떻게 받나요?',
    a: '3/3 완료 시 한정 일러스트 다운로드가 활성화 됩니다. 다운로드 링크는 발급 시점부터 7일간 유효해요.',
  },
  {
    q: '받은 일러스트를 공유하거나 재배포해도 되나요?',
    a: '코드와 일러스트는 개인 소장 용도로 제공됩니다. 무단 재배포·상업적 이용은 금지됩니다.',
  },
];
